
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToTryOnHistory, TryOnResult } from '../services/db';
import { UploadCloudIcon } from './icons';
import { WardrobeItem } from '../types';
import { motion } from 'framer-motion';

interface HistoryPanelProps {
    onGarmentSelect: (garmentFile: File, garmentInfo: WardrobeItem) => void;
    onHistoryItemSelect: (item: TryOnResult) => void;
    isLoading: boolean;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ onGarmentSelect, onHistoryItemSelect, isLoading }) => {
    const { user } = useAuth();
    const [history, setHistory] = useState<TryOnResult[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        if (user) {
            setLoadingHistory(true);
            unsubscribe = subscribeToTryOnHistory(user.uid, (data) => {
                setHistory(data);
                setLoadingHistory(false);
            });
        }

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [user]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const customGarmentInfo: WardrobeItem = {
                id: `custom-${Date.now()}`,
                name: file.name,
                url: URL.createObjectURL(file),
                price: 0,
                description: 'Custom item uploaded'
            };
            onGarmentSelect(file, customGarmentInfo);
        }
    };

    return (
        <div className="pt-6 border-t border-gray-400/50">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-serif tracking-wider text-gray-800">History / سجل التجارب</h2>
                <span className="text-xs text-gray-500">{history.length} items</span>
            </div>

            <div className="flex flex-col gap-6">
                <div>
                    <label
                        htmlFor="history-upload"
                        className={`group relative flex items-center justify-center p-4 border-2 border-dashed border-purple-300 hover:border-purple-500 rounded-xl cursor-pointer bg-purple-50/50 hover:bg-purple-100/50 transition-all duration-300 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 text-purple-600 rounded-full group-hover:scale-110 transition-transform">
                                <UploadCloudIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="font-semibold text-purple-900">Try New Outfit</p>
                                <p className="text-xs text-purple-600">Upload any image to try on</p>
                            </div>
                        </div>
                        <input
                            id="history-upload"
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={isLoading}
                        />
                    </label>
                </div>

                {loadingHistory ? (
                    <div className="flex justify-center p-8">
                        <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        {history.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="group relative rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                            >
                                <div className="aspect-[3/4] relative">
                                    <img
                                        src={item.imageUrl}
                                        alt="Try-on result"
                                        className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                                        onClick={() => onHistoryItemSelect(item)}
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3 pointer-events-none">
                                        <p className="text-white text-xs font-medium truncate">
                                            {new Date(item.timestamp.seconds * 1000).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}

                {!loadingHistory && history.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        <p>No history yet.</p>
                        <p className="text-xs mt-1">Try on an outfit to see it here!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HistoryPanel;
